import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ValidationRequest,
  ValidationRequestType,
} from '@app/features/contracts/validation/validation-request.model';
import { RUNTIME_CONFIG } from '@environments/runtime-config.token';
import { Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { AmendmentService } from './amendment.service';

/**
 * Le circuit d'un avenant : les mêmes échelons qu'une embauche, sur l'avenant seul
 * (RG-avenant-flot-de-validation). L'avenant est relu après chaque geste.
 */
@Injectable({ providedIn: 'root' })
export class AmendmentValidationCircuit {
  private http = inject(HttpClient);
  private amendmentService = inject(AmendmentService);
  private apiUrl = inject(RUNTIME_CONFIG).apiUrl;

  /** Les conditions d'un avenant sont vérifiées par l'API à chaque enregistrement. */
  verify(): Observable<void> {
    return of(undefined);
  }

  request(
    amendmentId: string,
    requestedTo: string,
    type: ValidationRequestType,
    comment: string | null,
  ): Observable<ValidationRequest> {
    return this.http
      .post<ValidationRequest>(this.url(amendmentId), { requestedTo, type, comment })
      .pipe(switchMap((request) => this.refresh(amendmentId, request)));
  }

  accept(amendmentId: string, refresh = true): Observable<ValidationRequest> {
    return this.http
      .post<ValidationRequest>(`${this.url(amendmentId)}/accept`, { comment: null })
      .pipe(switchMap((request) => (refresh ? this.refresh(amendmentId, request) : of(request))));
  }

  refuse(amendmentId: string, comment: string): Observable<ValidationRequest> {
    return this.http
      .post<ValidationRequest>(`${this.url(amendmentId)}/refuse`, { comment })
      .pipe(switchMap((request) => this.refresh(amendmentId, request)));
  }

  cancel(amendmentId: string, comment: string): Observable<ValidationRequest> {
    return this.http
      .post<ValidationRequest>(`${this.url(amendmentId)}/cancel`, { comment })
      .pipe(switchMap((request) => this.refresh(amendmentId, request)));
  }

  private url(amendmentId: string): string {
    return `${this.apiUrl}/amendments/${amendmentId}/validation-requests`;
  }

  private refresh(amendmentId: string, request: ValidationRequest): Observable<ValidationRequest> {
    return this.amendmentService.findOne(amendmentId).pipe(map(() => request));
  }
}
